import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Show banner after a short delay for better UX
            setTimeout(() => setShowBanner(true), 1000);
        }
        // If consent exists, it's already applied during Firebase init
    }, []);

    const handleAccept = () => {
        // Save consent
        localStorage.setItem('cookieConsent', 'accepted');
        
        // Update Firebase Analytics consent
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }
        
        // Log consent event (now that analytics is enabled)
        if (window.firebaseAnalytics) {
            const { logEvent } = require('firebase/analytics');
            logEvent(window.firebaseAnalytics, 'cookie_consent_granted');
        }
        
        setShowBanner(false);
    };

    const handleDecline = () => {
        // Save declined consent
        localStorage.setItem('cookieConsent', 'declined');
        
        // Ensure analytics stays denied
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied'
            });
        }
        
        setShowBanner(false);
    };

    if (!showBanner) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
            <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border-2 border-gray-200">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Cookie className="w-8 h-8 text-blue-600" />
                            <h3 className="text-xl font-bold text-gray-900">Cookie Notice</h3>
                        </div>
                        <button
                            onClick={handleDecline}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Main Content */}
                    <div className="mb-6">
                        <p className="text-gray-700 leading-relaxed">
                            We use cookies to provide authentication and improve your experience. 
                            By clicking <strong>"Accept All"</strong>, you consent to our use of analytics cookies 
                            to help us understand how you use the app and make improvements.
                        </p>

                        {/* Details Section (collapsible) */}
                        {showDetails && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-1">
                                        🔒 Essential Cookies (Required)
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Required for authentication and core functionality. These cannot be disabled.
                                    </p>
                                    <ul className="text-sm text-gray-600 list-disc list-inside mt-2">
                                        <li>Firebase Authentication</li>
                                        <li>Session management</li>
                                        <li>Security tokens</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-1">
                                        📊 Analytics Cookies (Optional)
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Help us understand how you use the app so we can improve it. These are optional and require your consent.
                                    </p>
                                    <ul className="text-sm text-gray-600 list-disc list-inside mt-2">
                                        <li>Firebase Analytics (Google Analytics 4)</li>
                                        <li>Page views and user interactions</li>
                                        <li>Performance metrics</li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                            {showDetails ? '▲ Hide Details' : '▼ Show Details'}
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleAccept}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                        >
                            Accept All
                        </button>
                        <button
                            onClick={handleDecline}
                            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
                        >
                            Essential Only
                        </button>
                    </div>

                    {/* Privacy Policy Link */}
                    <p className="mt-4 text-xs text-center text-gray-500">
                        Learn more in our{' '}
                        <a href="/privacy" className="text-blue-600 hover:underline">
                            Privacy Policy
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
